/**
 * Состояние виджета программы.
 *
 * Считается в одном месте и используется дважды: сервер рисует виджет
 * при отрисовке страницы, браузер обновляет его после опроса статуса.
 * Раньше тексты жили только в браузере, а разметка — только на сервере,
 * и после перезагрузки человек видел «Параметры были изменены» вместо
 * «идёт сборка» или «сборка не удалась» (тест 07.10).
 *
 * В режиме автогенерации состояний четыре (правило от 11.10):
 *
 * | состояние | когда | что делает человек |
 * |---|---|---|
 * | `running`  | сборка идёт прямо сейчас | ждёт, виджет показывает движение |
 * | `failed`   | не собралась, данных хватало | «Попробовать снова» |
 * | `blocked`  | не собралась, анкета неполная | заполняет анкету |
 * | `empty`    | программы ещё нет, данных хватает | «Создать программу» |
 * | `ready`    | программа собрана | видит дату и время сборки |
 * | `awaiting` | доступ не оплачен | оплачивает |
 *
 * У эксперта в карточке клиента `awaiting` выглядит иначе: «Клиент
 * не оплатил доступ» и без кнопки — платит клиент, не эксперт.
 *
 * Оплата проверяется первой: пока доступ не оплачен, остальные
 * состояния человеку ничего не дают.
 *
 * Из `blocked` сборка не запускается вовсе, поэтому после запуска
 * перейти в него нельзя: если сборка пошла, параметров хватало.
 */

// Сообщение сервиса сборки человеку ничего не говорит, поэтому
// переводим известные случаи.
const BUILD_ERRORS = {
    'No programms generated': 'Под текущие параметры и исключения не нашлось сочетаний',
};

const MONTHS = [
    'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
    'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

// «11 октября в 12:32». Дата нужна в позитивном состоянии: человек
// должен видеть, что программа свежая, а не «когда-то была».
function formatMoment(value) {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime())) return '';
    const pad = number => String(number).padStart(2, '0');
    return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()} в ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * @param {object} status ответ ручки programms-status
 * @returns {{state: string, title: string, note: string, buttonText: string,
 *            showButton: boolean, running: boolean, success: boolean}}
 */
function getRebuildWidgetState(status, options) {
    const value = status || {};
    const missing = value.missingParams || [];
    // Эксперт смотрит чужую программу: оплачивает её клиент, не он.
    // Поэтому вместо призыва к оплате — факт, и без кнопки.
    const viewerIsOwner = !options || options.viewerIsOwner !== false;

    // Пока доступ не оплачен, остальное неважно: программа может быть
    // собрана и даже свежая, но пользоваться ей нельзя. Поэтому
    // проверка первая — дата сборки вместо «Оплатить» вводила
    // в заблуждение (правка 12.10).
    if (value.subscriptionActive === false) {
        return {
            state: 'awaiting',
            title: viewerIsOwner ? 'Начните сейчас' : 'Клиент не оплатил доступ',
            note: '',
            buttonText: viewerIsOwner ? 'Оплатить' : '',
            showButton: viewerIsOwner,
            // Действие не «собрать заново», а «оплатить»: кнопка ведёт
            // на оплату, а не запускает сборку. У эксперта кнопки нет
            // вовсе — платит клиент (правка 12.10).
            payment: viewerIsOwner,
            running: false,
            success: false,
        };
    }

    if (value.buildState === 'running') {
        return {
            state: 'running',
            title: 'Создаём программу, до 2 минут',
            note: '',
            buttonText: '',
            showButton: false,
            running: true,
            success: false,
        };
    }

    if (value.buildState === 'failed') {
        // Анкета неполная — предлагать «ещё раз» бессмысленно, результат
        // будет тот же. Говорим, чего не хватает.
        if (missing.length) {
            return {
                state: 'blocked',
                title: `Не хватает данных: ${missing.join(', ')}`,
                note: '',
                buttonText: '',
                showButton: false,
                running: false,
                success: false,
            };
        }
        return {
            state: 'failed',
            title: 'Не удалось создать программу',
            note: '',
            buttonText: 'Повторить',
            showButton: true,
            running: false,
            success: false,
        };
    }

    if (value.hasProgramms) {
        const moment = formatMoment(value.lastProgrammsUpdate);
        return {
            state: 'ready',
            title: moment ? `Программа создана ${moment}` : 'Программа создана',
            note: '',
            buttonText: '',
            showButton: false,
            running: false,
            // Позитивное состояние само исчезает через пару секунд,
            // если виджет показан по сохранению.
            success: true,
        };
    }

    // Анкета неполная — программу собрать не из чего, и это единственное
    // состояние, где человеку надо идти заполнять поля.
    if (missing.length) {
        return {
            state: 'blocked',
            title: `Не хватает данных: ${missing.join(', ')}`,
            note: '',
            buttonText: '',
            showButton: false,
            running: false,
            success: false,
        };
    }

    // Программы ещё нет, а данных хватает. Прежде здесь висело
    // «программа появится, когда будут заполнены параметры» — неправда,
    // параметры заполнены, и человек ждал неизвестно чего. Говорим факт
    // и даём действие: сборка запускается кнопкой.
    //
    // Такое состояние встречается и когда в профиле висит ссылка
    // на исчезнувшую программу: тогда статус приходит с `hasProgramms`,
    // и страница сама его поправляет (withLoadedProgramms в роутере).
    return {
        state: 'empty',
        title: 'Программа ещё не создана',
        note: '',
        buttonText: value.canBuild === false ? '' : 'Создать программу',
        showButton: value.canBuild !== false,
        running: false,
        success: false,
    };
}

module.exports = {
    getRebuildWidgetState,
    formatMoment,
    BUILD_ERRORS,
};
